export const VERT = `#version 300 es
precision highp float;
layout(location = 0) in vec3 a_p0;
layout(location = 1) in vec3 a_p1;

uniform int   u_is3d;
uniform mat3  u_rot;        // 3D: two-axis rotation
uniform vec2  u_axy;        // 2D: aspect stretch (k, 1/k)
uniform float u_scale;      // CSS px per world unit
uniform vec2  u_vc;         // world (centered) point at the viewport centre (after rotation in 3D)
uniform vec2  u_centerPx;   // viewport centre in CSS px
uniform float u_dpr;
uniform vec2  u_viewport;   // device px
uniform float u_halfWidth;  // device px
uniform float u_stride;     // original-index step between consecutive points in this buffer
uniform float u_invNm1;     // 1 / (n_points - 1)
uniform float u_first;      // buffer index of the first drawn segment
uniform float u_lastInst;   // instance id of the last drawn segment
uniform vec2  u_clip;       // fraction of the first segment to skip, fraction of the last to keep
uniform int   u_persp;      // 3D: perspective (1) or orthographic (0)
uniform float u_invExtent;  // 3D: 1 / (half the largest bbox span)

out vec2  v_pos;
out vec2  v_s0;
out vec2  v_s1;
out float v_t;
out float v_depth;          // 3D: normalised depth toward the viewer, -1..1

// Returns screen position (device px) and normalised depth.
vec3 project(vec3 p) {
  vec2 q;
  float depth = 0.0;
  if (u_is3d == 1) {
    vec3 r = u_rot * p;
    depth = clamp(r.z * u_invExtent, -3.0, 3.0);
    float f = u_persp == 1 ? 4.5 / (4.5 - depth) : 1.0;   // camera 4.5 extents away, like the Atlas page
    q = vec2(r.x, -r.y) * f;
  } else {
    q = vec2(p.x * u_axy.x, -p.y * u_axy.y);
  }
  return vec3(((q - u_vc) * u_scale + u_centerPx) * u_dpr, depth);
}

void main() {
  float inst = float(gl_InstanceID);
  float fLo = inst == 0.0 ? u_clip.x : 0.0;
  float fHi = inst == u_lastInst ? u_clip.y : 1.0;
  vec3 q0 = project(mix(a_p0, a_p1, fLo));
  vec3 q1 = project(mix(a_p0, a_p1, fHi));
  vec2 s0 = q0.xy;
  vec2 s1 = q1.xy;
  vec2 d = s1 - s0;
  float len = length(d);
  vec2 dir = len > 1e-6 ? d / len : vec2(1.0, 0.0);
  vec2 nrm = vec2(-dir.y, dir.x);
  int vid = gl_VertexID;
  float end  = (vid == 2 || vid == 3 || vid == 5) ? 1.0 : 0.0;
  float side = (vid == 1 || vid == 4 || vid == 5) ? 1.0 : -1.0;
  float ext = u_halfWidth + 1.0;   // 1 px AA margin; also forms the round caps
  vec2 base = mix(s0, s1, end) + dir * (end * 2.0 - 1.0) * ext + nrm * side * ext;
  float i0 = (u_first + inst) * u_stride;
  float t0 = min(1.0, (i0 + fLo * u_stride) * u_invNm1);
  float t1 = min(1.0, (i0 + fHi * u_stride) * u_invNm1);
  v_t = mix(t0, t1, end);
  v_depth = mix(q0.z, q1.z, end);
  v_pos = base; v_s0 = s0; v_s1 = s1;
  vec2 clip = vec2(base.x / u_viewport.x * 2.0 - 1.0, 1.0 - base.y / u_viewport.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
}
`;

export const FRAG = `#version 300 es
precision highp float;
in vec2  v_pos;
in vec2  v_s0;
in vec2  v_s1;
in float v_t;
in float v_depth;

uniform int   u_depthCue;   // 3D: darken strands that are farther away
uniform float u_alpha;      // overall opacity
uniform int   u_mode;       // 0 ramp, 1 helix, 2 solid
uniform sampler2D u_ramp;
uniform float u_rampW;
uniform float u_reps;
uniform float u_rampRot;
uniform float u_m;          // closed stops - 1
uniform int   u_pingpong;
uniform int   u_hard;
uniform vec4  u_helix;      // Lmin, Lmax, C, turns
uniform vec3  u_solid;
uniform float u_halfWidth;
uniform float u_alphaScale;

out vec4 fragColor;

float sdSegment(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-12), 0.0, 1.0);
  return length(pa - ba * h);
}

vec3 rampColor(float t) {
  float tr = t + u_rampRot;
  float v = (tr - floor(tr)) * u_reps;
  float u;
  if (u_pingpong == 1 && u_reps > 1.0) {
    float c = floor(v);
    u = v - c;
    if (mod(c, 2.0) >= 1.0) u = 1.0 - u;
  } else {
    u = v - floor(v);
  }
  if (u_hard == 1) u = floor(u * u_m + 0.5) / u_m;
  return texture(u_ramp, vec2((u * (u_rampW - 1.0) + 0.5) / u_rampW, 0.5)).rgb;
}

float labInv(float f) { float c = f * f * f; return c > 0.008856 ? c : (f - 16.0 / 116.0) / 7.787; }
float srgbEnc(float v) { return v <= 0.0031308 ? 12.92 * v : 1.055 * pow(abs(v), 1.0 / 2.4) - 0.055; }
vec3 labToRgb(float L, float A, float B) {
  float fy = (L + 16.0) / 116.0, fx = fy + A / 500.0, fz = fy - B / 200.0;
  float X = labInv(fx) * 0.95047, Y = labInv(fy), Z = labInv(fz) * 1.08883;
  float r = X *  3.2406 + Y * -1.5372 + Z * -0.4986;
  float g = X * -0.9689 + Y *  1.8758 + Z *  0.0415;
  float b = X *  0.0557 + Y * -0.2040 + Z *  1.0570;
  return clamp(vec3(srgbEnc(r), srgbEnc(g), srgbEnc(b)), 0.0, 1.0);
}
vec3 helixColor(float t) {
  float u = clamp(t, 0.0, 1.0);
  float L = mix(u_helix.x, u_helix.y, u);
  float h = 6.283185307 * u_helix.w * u;
  return labToRgb(L, u_helix.z * cos(h), u_helix.z * sin(h));
}

void main() {
  float d = sdSegment(v_pos, v_s0, v_s1);
  float a = clamp(u_halfWidth + 0.5 - d, 0.0, 1.0) * u_alphaScale * u_alpha;
  if (a <= 0.0) discard;
  vec3 c = u_mode == 2 ? u_solid : (u_mode == 1 ? helixColor(v_t) : rampColor(v_t));
  if (u_depthCue == 1) c *= clamp(0.80 + v_depth * 0.19, 0.48, 1.0);
  fragColor = vec4(c * a, a);
}
`;
